import { z } from 'zod';
import { amountScale, currencyCodeSchema } from './currency.js';
import { addMoney, compareMoney, exchangeRateSchema, fitsCurrencyScale, isNegativeMoney, isZeroMoney, moneyAmountSchema, } from './money.js';
/**
 * Recording money (Phase 3 PR 2): expenses and income (one or several category lines, a "split"),
 * and transfers between two accounts in the same currency. Every one becomes a journal
 * transaction whose postings sum to zero per currency (CLAUDE.md §3.2). Amounts here are in
 * everyday terms, always positive; the API turns them into signed postings.
 */
export const TRANSACTION_KINDS = [
    'expense',
    'income',
    'transfer',
    'opening_balance',
    'reversal',
    // Phase 4: debts (never income or expense). A debt between two members is stored from the
    // lender's side (lend, collect); each viewer's label comes from `debt` on the transaction.
    'borrow',
    'lend',
    'repay',
    'collect',
    'debt_opening',
    // Phase 4 PR 3: one currency for another, through the FX trading accounts.
    'exchange',
];
export const transactionKindSchema = z.enum(TRANSACTION_KINDS);
/** The kinds people record from the Add screen. */
export const RECORDABLE_KINDS = ['expense', 'income', 'transfer'];
/** A calendar day, YYYY-MM-DD (in the family's time zone). */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Date must look like 2026-10-20')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Not a real date');
const isMoneyString = (v) => typeof v === 'string' && moneyAmountSchema.safeParse(v).success;
/** A positive amount: what was spent, earned or moved. */
export const positiveAmountSchema = moneyAmountSchema.refine(
// Zod runs refinements even after the format check failed, so check the format first.
(v) => isMoneyString(v) && !isNegativeMoney(v) && !isZeroMoney(v), 'Amount must be more than zero');
export const descriptionSchema = z.string().trim().max(200);
export const lineNoteSchema = z.string().trim().max(200);
export const payeeNameSchema = z.string().trim().min(1).max(80);
/** One category line of an expense or income; several lines make a split. */
export const categoryLineSchema = z.object({
    categoryId: z.string().uuid(),
    amount: positiveAmountSchema,
    note: lineNoteSchema.optional(),
});
const common = {
    /** The day it happened in the family's time zone; defaults to today. Never in the future. */
    occurredOn: isoDateSchema.optional(),
    description: descriptionSchema.optional(),
    /**
     * The transaction's currency. Every line uses it, and the account(s) must hold it.
     */
    currency: currencyCodeSchema,
    /**
     * A one-off rate for this transaction only (1 base = rate), e.g. a money changer's rate.
     * Without it the family rate in effect on `occurredOn` is used. Not allowed for the base
     * currency, whose rate is always 1.
     */
    rate: exchangeRateSchema.optional(),
    /**
     * Recording an occurrence of a recurring template (Phase 5): the template and the due date.
     * The kind must match the template's; one occurrence is recorded once.
     */
    recurring: z.object({ templateId: z.string().uuid(), dueOn: isoDateSchema }).optional(),
};
/** Zero or more: what was paid now on a purchase partly on credit. */
const paidNowSchema = moneyAmountSchema.refine((v) => isMoneyString(v) && !isNegativeMoney(v), 'Amount cannot be negative');
/**
 * Part on credit: of the total, `paidNow` moves through the account now (it may be 0) and the
 * rest is owed to (expense) or by (income) an outside person, as a debt linked to this
 * transaction. When `paidNow` is the whole total, nothing is on credit and no debt is created.
 */
export const creditPartSchema = z.object({
    paidNow: paidNowSchema,
    /** The person from the people list, or a new person by name. */
    personId: z.string().uuid().optional(),
    personName: payeeNameSchema.optional(),
    /** Optional: when the credit should be settled (shows in Due soon). */
    dueOn: isoDateSchema.optional(),
});
const expenseOrIncomeSchema = z.object({
    ...common,
    kind: z.enum(['expense', 'income']),
    /** The money account (as people see it) the money left or arrived in. */
    accountId: z.string().uuid(),
    /** The total. With one line it equals that line; with a split the lines must add up to it. */
    amount: positiveAmountSchema,
    lines: z.array(categoryLineSchema).min(1).max(20),
    /** Who was paid (or paid you): an existing payee, or a name to find or create. */
    payeeId: z.string().uuid().optional(),
    payeeName: payeeNameSchema.optional(),
    /**
     * For a payment from a private account: let the family see the transaction (amount and
     * category); the account itself stays hidden.
     */
    sharedWithFamily: z.boolean().default(false),
    /** Part (or all) of it on credit with an outside person. */
    credit: creditPartSchema.optional(),
});
const transferSchema = z.object({
    ...common,
    kind: z.literal('transfer'),
    fromAccountId: z.string().uuid(),
    toAccountId: z.string().uuid(),
    amount: positiveAmountSchema,
});
/** POST /api/v1/transactions (Idempotency-Key required). */
export const createTransactionRequestSchema = z
    .discriminatedUnion('kind', [expenseOrIncomeSchema, transferSchema])
    .superRefine((v, ctx) => {
    // Zod may run this even when a field failed, so only look at well-formed values.
    if (v.kind !== 'transfer' && !Array.isArray(v.lines))
        return;
    const paidNow = v.kind !== 'transfer' ? v.credit?.paidNow : undefined;
    const all = v.kind === 'transfer'
        ? [v.amount]
        : [v.amount, ...v.lines.map((l) => l.amount), ...(paidNow === undefined ? [] : [paidNow])];
    if (!all.every(isMoneyString))
        return;
    const scale = amountScale(v.currency);
    const amounts = v.kind === 'transfer'
        ? [{ path: ['amount'], amount: v.amount }]
        : [
            { path: ['amount'], amount: v.amount },
            ...v.lines.map((l, i) => ({ path: ['lines', i, 'amount'], amount: l.amount })),
            ...(paidNow === undefined ? [] : [{ path: ['credit', 'paidNow'], amount: paidNow }]),
        ];
    for (const { path, amount } of amounts) {
        if (!fitsCurrencyScale(amount, scale)) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: scale === 0
                    ? `${v.currency} amounts must be whole numbers`
                    : `${v.currency} amounts allow at most ${scale} decimals`,
            });
        }
    }
    if (v.kind === 'transfer') {
        if (v.fromAccountId === v.toAccountId) {
            ctx.addIssue({
                code: 'custom',
                path: ['toAccountId'],
                message: 'Pick two different accounts',
            });
        }
        return;
    }
    if (compareMoney(addMoney(...v.lines.map((l) => l.amount)), v.amount) !== 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['lines'],
            message: 'The split lines must add up to the total',
        });
    }
    if (v.credit) {
        const onCredit = compareMoney(v.credit.paidNow, v.amount) < 0;
        if (compareMoney(v.credit.paidNow, v.amount) > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['credit', 'paidNow'],
                message: 'Paid now cannot be more than the total',
            });
        }
        const people = [v.credit.personId, v.credit.personName].filter((x) => x !== undefined);
        if (onCredit && people.length !== 1) {
            ctx.addIssue({
                code: 'custom',
                path: ['credit', 'personId'],
                message: 'Pick the person',
            });
        }
        if (v.recurring !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['credit'],
                message: 'A recurring transaction cannot be on credit',
            });
        }
    }
    if (v.payeeId !== undefined && v.payeeName !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['payeeName'],
            message: 'Give a payee id or a payee name, not both',
        });
    }
});
/**
 * One posting as the viewer may see it. A line on a money account the viewer cannot see is
 * masked: no account and no amounts (CLAUDE.md §3.5).
 */
export const transactionLineSchema = z.object({
    /**
     * money = a money account; debt = a debt's own account (masked like money); category =
     * income/expense category; other = bookkeeping.
     */
    role: z.enum(['money', 'debt', 'category', 'other']),
    masked: z.boolean(),
    /** For a money line, the account as people see it (the account group id). */
    accountId: z.string().uuid().nullable(),
    accountName: z.string().nullable(),
    currency: currencyCodeSchema,
    /** Signed: + debit, - credit. Null when masked. */
    amount: moneyAmountSchema.nullable(),
    /** The same in the base currency, from the transaction's own rate. Null when masked. */
    baseAmount: moneyAmountSchema.nullable(),
    note: z.string().nullable(),
});
export const transactionSchema = z.object({
    id: z.string().uuid(),
    kind: transactionKindSchema,
    occurredOn: z.string(),
    recordedAt: z.string(),
    description: z.string(),
    payee: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    sharedWithFamily: z.boolean(),
    reversesTransactionId: z.string().uuid().nullable(),
    /** The correction (reversal) of this transaction, if it was corrected. */
    reversedByTransactionId: z.string().uuid().nullable(),
    createdBy: z.object({ memberId: z.string().uuid(), displayName: z.string() }),
    /**
     * In everyday terms, always positive: what was spent, earned or moved, in `currency`, and the
     * same in the base currency at the transaction's own rate. Null when the viewer cannot see
     * enough of the transaction to know it.
     */
    currency: currencyCodeSchema,
    amount: moneyAmountSchema.nullable(),
    baseAmount: moneyAmountSchema.nullable(),
    /** The rate snapshot per currency (1 base = rate); the base currency is "1". */
    rates: z.record(z.string(), exchangeRateSchema),
    lines: z.array(transactionLineSchema),
    /** An expense paid in another currency without a bill: estimated with the family rate. */
    estimatedWithFamilyRate: z.boolean().default(false),
    /**
     * Detail only (GET /transactions/:id), for a transaction in two currencies: the family rate
     * per currency on its day, to compare with the rate the deal used. Null elsewhere.
     */
    familyRates: z.record(z.string(), exchangeRateSchema).nullable().default(null),
    /** For a debt transaction the viewer can see a debt of: how it reads for them. */
    debt: z
        .object({
        debtId: z.string().uuid(),
        label: z.enum(['borrowed', 'lent', 'repaid', 'collected', 'existing_owed', 'existing_owing']),
        /** The other side: the outside person's or the other member's name. */
        personName: z.string(),
        /** The interest or fee part of a repayment (an expense), in `currency`. */
        interest: moneyAmountSchema.nullable(),
    })
        .nullable(),
    /**
     * An expense or income partly on credit (or its correction), when the viewer can see its
     * debt line: the person, what is on credit and what was paid now, in `currency`.
     */
    credit: z
        .object({
        debtId: z.string().uuid(),
        personId: z.string().uuid(),
        personName: z.string(),
        onCredit: moneyAmountSchema,
        /** Null when the money line is on an account the viewer cannot see. */
        paidNow: moneyAmountSchema.nullable(),
        dueOn: z.string().nullable(),
    })
        .nullable()
        .default(null),
    /** Recorded for an occurrence of a recurring template; `auto`: recorded automatically. */
    recurring: z
        .object({
        templateId: z.string().uuid(),
        name: z.string(),
        dueOn: z.string(),
        auto: z.boolean(),
    })
        .nullable()
        .default(null),
});
/** Activity filters: "debt" means every debt kind. */
export const TRANSACTION_FILTER_KINDS = [
    'expense',
    'income',
    'transfer',
    'exchange',
    'debt',
];
export const transactionListQuerySchema = z.object({
    cursor: z.string().max(200).optional(),
    kind: z.enum(TRANSACTION_FILTER_KINDS).optional(),
    /** A money account as people see it (account group id); it must be one the viewer can see. */
    accountId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
    /** A merged payee finds its survivor's transactions too. */
    payeeId: z.string().uuid().optional(),
    /** Recorded by this member. */
    memberId: z.string().uuid().optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    /** Words in the note or the payee's name. */
    q: z.string().trim().max(80).optional(),
    limit: z
        .string()
        .regex(/^[1-9]\d?$|^100$/, 'limit must be 1 to 100')
        .default('30')
        .transform((v) => Number.parseInt(v, 10)),
});
export const transactionListResponseSchema = z.object({
    items: z.array(transactionSchema),
    nextCursor: z.string().nullable(),
});
/**
 * What the Add screen starts with, from the viewer's own recent transactions: the account they
 * used last per kind, and the currency they used last on each account.
 */
export const transactionDefaultsSchema = z.object({
    lastAccountId: z.object({
        expense: z.string().uuid().nullable(),
        income: z.string().uuid().nullable(),
        transfer: z.string().uuid().nullable(),
    }),
    lastCurrencyByAccount: z.record(z.string(), currencyCodeSchema),
});
/** POST /api/v1/transactions/:id/reverse (Idempotency-Key required): the first half of Correct. */
export const reverseTransactionRequestSchema = z.object({
    note: z.string().trim().max(200).optional(),
});
// ---------------------------------------------------------------------------------------------
// Payees
// ---------------------------------------------------------------------------------------------
export const payeeSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /**
     * The category to pre-fill: the payee's default category if a parent set one, else the last
     * category used with this payee in a transaction the viewer can see.
     */
    suggestedCategoryId: z.string().uuid().nullable(),
    /** Uses in transactions the viewer can see (last 365 days). */
    usageCount: z.number().int(),
    /** The category a parent set for this payee, if any. */
    defaultCategoryId: z.string().uuid().nullable().default(null),
    archived: z.boolean().default(false),
});
export const payeeListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    includeArchived: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    limit: z
        .string()
        .regex(/^[1-9]\d?$|^100$/, 'limit must be 1 to 100')
        .default('20')
        .transform((v) => Number.parseInt(v, 10)),
});
export const payeeListResponseSchema = z.object({ items: z.array(payeeSchema) });
export const createPayeeRequestSchema = z.object({ name: payeeNameSchema });
/** PATCH /api/v1/payees/:id (payees.manage). */
export const updatePayeeRequestSchema = z
    .object({
    name: payeeNameSchema.optional(),
    /** The category to pre-fill for this payee; null clears it. */
    defaultCategoryId: z.string().uuid().nullable().optional(),
})
    .refine((v) => v.name !== undefined || v.defaultCategoryId !== undefined, 'Nothing to change');
/** POST /api/v1/payees/:id/merge (payees.manage): the duplicate's transactions read as `intoId`. */
export const mergePayeeRequestSchema = z.object({ intoId: z.string().uuid() });
//# sourceMappingURL=transactions.js.map